import { useInfiniteQuery } from '@tanstack/react-query';
import { queryKeys, type Locale, type VideoCard } from '@nk/shared';
import { publicApi, type CursorResult } from '@/lib/api-client';
import { STALE } from '@/lib/query';

/**
 * `/videos`, paged the same way every other feed is — cursor-based (§2), through
 * `useInfiniteQuery`, so a reader who came back from a video still has the rows they had
 * scrolled to.
 *
 * Separate from `useFeed` because the payload is a different type and the filter set is
 * one field; sharing them would mean a generic with two call sites.
 */
export function useVideos(locale: Locale, category?: string) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.videos(locale, category),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      publicApi.videos({
        locale,
        ...(category ? { category } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    getNextPageParam: (lastPage: CursorResult<VideoCard>) => lastPage.nextCursor,
    staleTime: STALE.feed,
  });

  return {
    ...query,
    videos: query.data?.pages.flatMap((page) => page.items) ?? [],
  };
}
